/* This method groups activity items
 It takes a sorted array of activity items (with the same structure as the API response)
 It outputs a sorted array of activity items where all, some or none of them are grouped into groups (depending on the grouping configuration).
 Activity item groups are activity items of a specific group type, all the grouped activity item IDs in that group will be inside the metadata.
 The id of the group is "group-${most recent activity item id}"
 Example:
 {
    "id": "group-activity-item-id-3",
    "date": "2020-11-25T11:27:19+0000",
    "type": "answer_changed_group",
    "orgID": "org-id-1",
    "private": false,
    "metadata": {
        "userID":"user-id-1", //User ID common to all items (if there is one)
        "groupedActivityItems": [
             'activity-item-id-3',
             'activity-item-id-2',
             'activity-item-id-1',
             ....
         ]
    }
 }
 */

import type { ActivityItem, ActivityType } from '@@/packages/shared/types/models/activity';
import type { FeedItemType, GroupActivityItem, GroupActivityType } from './types';

type GroupDefinition = {
  name: GroupActivityType;
  itemTypes: readonly ActivityType[];
  rollingWindowMinutes: number;
  // Item or group types that close an open group of this kind when they
  // appear between its members.
  blockedBy?: readonly FeedItemType[];
  // Extra condition on top of itemTypes, for groups split on a metadata value.
  matches?: (item: ActivityItem) => boolean;
};

// Groups should never overlap (grouping the same item): only the first defined
// group gets it.
const definedGroups: readonly GroupDefinition[] = [
  {
    name: 'answer_changed_group',
    itemTypes: [
      'answer_changed',
      'notes_changed',
      'evidence_added',
      'evidence_removed',
      'control_update_confirmed',
    ],
    rollingWindowMinutes: 5,
  },
  {
    name: 'scoping_answer_changed_group',
    itemTypes: ['scoping_answer_changed'],
    rollingWindowMinutes: 5,
  },
  {
    name: 'remediation_requested_group',
    itemTypes: ['remediation_requested'],
    rollingWindowMinutes: 5,
    blockedBy: [
      'remediation_responded_group',
      'remediation_cancelled_group',
      'remediation_rejected_group',
      'remediation_completed_group',
    ],
  },
  {
    name: 'remediation_responded_group',
    itemTypes: ['remediation_responded'],
    rollingWindowMinutes: 5,
    blockedBy: [
      'remediation_requested_group',
      'remediation_cancelled_group',
      'remediation_rejected_group',
      'remediation_completed_group',
    ],
  },
  {
    name: 'remediation_cancelled_group',
    itemTypes: ['remediation_cancelled'],
    rollingWindowMinutes: 5,
    blockedBy: [
      'remediation_requested_group',
      'remediation_responded_group',
      'remediation_rejected_group',
      'remediation_completed_group',
    ],
  },
  {
    name: 'remediation_rejected_group',
    itemTypes: ['remediation_rejected'],
    rollingWindowMinutes: 5,
    blockedBy: [
      'remediation_requested_group',
      'remediation_responded_group',
      'remediation_cancelled_group',
      'remediation_completed_group',
    ],
  },
  {
    name: 'remediation_completed_group',
    itemTypes: ['remediation_completed'],
    rollingWindowMinutes: 5,
    blockedBy: [
      'remediation_requested_group',
      'remediation_responded_group',
      'remediation_cancelled_group',
      'remediation_rejected_group',
    ],
  },
  {
    name: 'exemption_applied_group',
    itemTypes: ['override_applied'],
    rollingWindowMinutes: 5,
    matches: (item) =>
      item.type === 'override_applied' && item.metadata.overrideType === 'exemption',
    blockedBy: [
      'exemption_removed_group',
      'non_compliance_applied_group',
      'non_compliance_removed_group',
    ],
  },
  {
    name: 'exemption_removed_group',
    itemTypes: ['override_removed'],
    rollingWindowMinutes: 5,
    matches: (item) =>
      item.type === 'override_removed' && item.metadata.overrideType === 'exemption',
    blockedBy: [
      'exemption_applied_group',
      'non_compliance_applied_group',
      'non_compliance_removed_group',
    ],
  },
  {
    name: 'non_compliance_applied_group',
    itemTypes: ['override_applied'],
    rollingWindowMinutes: 5,
    matches: (item) =>
      item.type === 'override_applied' && item.metadata.overrideType === 'noncompliance',
    blockedBy: [
      'exemption_applied_group',
      'exemption_removed_group',
      'non_compliance_removed_group',
    ],
  },
  {
    name: 'non_compliance_removed_group',
    itemTypes: ['override_removed'],
    rollingWindowMinutes: 5,
    matches: (item) =>
      item.type === 'override_removed' && item.metadata.overrideType === 'noncompliance',
    blockedBy: [
      'exemption_applied_group',
      'exemption_removed_group',
      'non_compliance_applied_group',
    ],
  },
  {
    name: 'control_compliance_changed_group',
    itemTypes: ['compliance_changed'],
    rollingWindowMinutes: 5,
  },
  {
    name: 'connection_compliance_changed_group',
    itemTypes: ['connection_compliance_changed'],
    rollingWindowMinutes: 5,
  },
  {
    name: 'policy_requirement_changed_group',
    itemTypes: ['policy_requirement_changed'],
    rollingWindowMinutes: 5,
  },
  {
    name: 'user_assignment_group',
    itemTypes: ['user_assigned', 'user_unassigned'],
    rollingWindowMinutes: 5,
  },
  {
    name: 'label_assignment_group',
    itemTypes: ['label_assigned', 'label_unassigned'],
    rollingWindowMinutes: 5,
  },
  {
    name: 'supplier_assessment_framework_updated_group',
    itemTypes: ['control_created', 'control_question_meaning_changed', 'control_deprecated'],
    rollingWindowMinutes: 5,
  },
  {
    name: 'approval_reviewer_added_group',
    itemTypes: ['approval_reviewer_added'],
    blockedBy: ['approval_reviewer_removed', 'approval_reviewer_responded'],
    rollingWindowMinutes: 5,
  },
  {
    name: 'approval_reviewer_removed_group',
    itemTypes: ['approval_reviewer_removed'],
    blockedBy: ['approval_reviewer_added', 'approval_reviewer_responded'],
    rollingWindowMinutes: 5,
  },
  // Requesting several approvers in one call emits one item per approver.
  {
    name: 'decision_requested_group',
    itemTypes: ['decision_requested'],
    blockedBy: ['decision_removed', 'decision_decided'],
    rollingWindowMinutes: 5,
  },
  {
    name: 'decision_removed_group',
    itemTypes: ['decision_removed'],
    blockedBy: ['decision_requested', 'decision_decided'],
    rollingWindowMinutes: 5,
  },
];

// A group under construction. Members are kept whole until every pass has run,
// because later passes need their dates and types; the emitted item only
// carries their IDs.
type PendingGroup = { definition: GroupDefinition; members: ActivityItem[] };

type PassItem = ActivityItem | PendingGroup;

const isPending = (item: PassItem): item is PendingGroup => 'members' in item;

const belongsToGroup = (definition: GroupDefinition, item: ActivityItem) =>
  definition.itemTypes.includes(item.type) && (definition.matches?.(item) ?? true);

// Items arrive newest first, so the oldest member is the last one.
const insideRollingWindow = (group: PendingGroup, item: ActivityItem) => {
  const oldest = group.members.at(-1);
  if (!oldest) return false;
  const gap = Math.abs(Date.parse(oldest.date) - Date.parse(item.date));
  return gap < group.definition.rollingWindowMinutes * 60000;
};

const blocksGroup = (definition: GroupDefinition, item: PassItem) =>
  (definition.blockedBy ?? []).some((blocking) => {
    if (isPending(item)) return item.definition.name === blocking;
    if (item.type === blocking) return true;
    const blockingDefinition = definedGroups.find((g) => g.name === blocking);
    return blockingDefinition !== undefined && belongsToGroup(blockingDefinition, item);
  });

const runPass = (items: PassItem[], definition: GroupDefinition): PassItem[] => {
  const output: PassItem[] = [];
  let current: PendingGroup | undefined;

  for (const item of items) {
    if (current && blocksGroup(definition, item)) {
      current = undefined;
    }

    if (isPending(item) || !belongsToGroup(definition, item)) {
      output.push(item);
      continue;
    }

    if (current && insideRollingWindow(current, item)) {
      current.members.push(item);
    } else {
      current = { definition, members: [item] };
      output.push(current);
    }
  }

  return output;
};

const actorOf = (item: ActivityItem) =>
  'userID' in item.metadata ? item.metadata.userID : undefined;

const emitGroup = (group: PendingGroup): ActivityItem | GroupActivityItem => {
  const [newest] = group.members;
  if (!newest) throw new Error('Activity item group has no members');
  if (group.members.length === 1) return newest;

  const firstUserID = actorOf(newest);
  const userID =
    firstUserID && group.members.every((member) => actorOf(member) === firstUserID)
      ? firstUserID
      : undefined;

  return {
    id: `group-${newest.id}`,
    date: newest.date,
    type: group.definition.name,
    orgID: newest.orgID,
    connectionID: newest.connectionID,
    private: newest.private,
    metadata: {
      ...(userID !== undefined && { userID }),
      groupedActivityItems: group.members.map((member) => member.id),
    },
  };
};

export const groupProfileLevelActivityItems = (
  activityItems: ActivityItem[],
): (ActivityItem | GroupActivityItem)[] =>
  definedGroups
    .reduce<PassItem[]>(runPass, activityItems)
    .map((item) => (isPending(item) ? emitGroup(item) : item));
